import { supabase } from '@/integrations/supabase/client';
import { SugarBabyProfile, SugarDaddyProfile, User, LifestyleInterestTag, ArrangementTypeTag, SdSupportTag } from '@/types';

// Type helper pour les retours de fonction
type ServiceResult<T = null> = 
  | { success: true; data: T; error: null }
  | { success: false; data: null; error: any };

type PaginationOptions = { page?: number; pageSize?: number };

type SugarBabyFilters = {
    locationCity?: string;
    lifestyleInterests?: LifestyleInterestTag[];
    arrangementTypes?: ArrangementTypeTag[];
};

type SugarDaddyFilters = {
    locationCity?: string;
    ageRange?: string; // Could be more granular later (min/max)
    lifestyleInterests?: LifestyleInterestTag[];
    supportTypes?: SdSupportTag[];
    arrangementTypes?: ArrangementTypeTag[];
    verificationStatus?: 'fully_verified'; // Filter only verified?
};

/**
 * Fetches detailed Sugar Baby profile information using RPC.
 * Includes associated user data (display_name, is_verified, role).
 * Respects RLS defined in the RPC function.
 */
export const getSugarBabyProfileDetails = async (
  profileId: string
): Promise<ServiceResult<SugarBabyProfile & { user: Partial<User> }>> => {
  try {
    const { data, error } = await supabase.rpc('get_sugar_baby_profile_details', {
      profile_id: profileId,
    });

    if (error) {
      console.error('Error fetching SB profile details (RPC):', error);
      return { success: false, data: null, error };
    }
    if (!data) {
         // This can happen if RLS prevents access or profile doesn't exist
         console.log('No SB profile details returned by RPC (อาจจะไม่มีสิทธิ์เข้าถึง):', profileId);
         return { success: false, data: null, error: 'Profile not found or access denied.' };
    }

    // We might need to map the JSONB fields or array types if RPC doesn't auto-cast perfectly
    // For example, ensure lifestyle_interests is an array, photos/videos/location/expectations are objects
    // Supabase typically handles basic JSONB well, but check nested structures.
    const profileData = data as any; // Cast to any temporarily for mapping if needed

    // Example mapping (adjust based on actual RPC return structure if needed)
    const mappedProfile: SugarBabyProfile & { user: Partial<User> } = {
        ...profileData,
        // Ensure nested JSON/arrays are correctly typed if necessary
        lifestyleInterests: Array.isArray(profileData.lifestyle_interests) ? profileData.lifestyle_interests : [],
        location: typeof profileData.location === 'object' ? profileData.location : {},
        photos: typeof profileData.photos === 'object' ? profileData.photos : undefined,
        videos: typeof profileData.videos === 'object' ? profileData.videos : undefined,
        arrangementExpectations: typeof profileData.arrangement_expectations === 'object' ? profileData.arrangement_expectations : {},
        user: typeof profileData.user === 'object' ? {
            id: profileData.user.id,
            displayName: profileData.user.display_name,
            isVerified: profileData.user.is_verified,
            role: profileData.user.role
        } : {}
    };


    return { success: true, data: mappedProfile, error: null };
  } catch (err) {
    console.error('Unexpected error in getSugarBabyProfileDetails:', err);
    return { success: false, data: null, error: err };
  }
};

/**
 * Fetches detailed Sugar Daddy profile information using RPC.
 * Includes associated user data (display_name, is_verified, subscription_tier, role).
 * Respects RLS defined in the RPC function.
 */
export const getSugarDaddyProfileDetails = async (
  profileId: string
): Promise<ServiceResult<SugarDaddyProfile & { user: Partial<User> }>> => {
  try {
    const { data, error } = await supabase.rpc('get_sugar_daddy_profile_details', {
      profile_id: profileId,
    });

    if (error) {
      console.error('Error fetching SD profile details (RPC):', error);
      return { success: false, data: null, error };
    }
     if (!data) {
         // This can happen if RLS prevents access or profile doesn't exist
         console.log('No SD profile details returned by RPC (อาจจะไม่มีสิทธิ์เข้าถึง):', profileId);
         return { success: false, data: null, error: 'Profile not found or access denied.' };
    }

    // Similar mapping/type checking as above might be needed
    const profileData = data as any; 

     // Example mapping (adjust based on actual RPC return structure if needed)
    const mappedProfile: SugarDaddyProfile & { user: Partial<User> } = {
        ...profileData,
        lifestyleInterests: Array.isArray(profileData.lifestyle_interests) ? profileData.lifestyle_interests : [],
        location: typeof profileData.location === 'object' ? profileData.location : {},
        photos: typeof profileData.photos === 'object' ? profileData.photos : undefined,
        offeredArrangement: typeof profileData.offered_arrangement === 'object' ? profileData.offered_arrangement : {},
        user: typeof profileData.user === 'object' ? {
            id: profileData.user.id,
            displayName: profileData.user.display_name,
            isVerified: profileData.user.is_verified,
            subscriptionTier: profileData.user.subscription_tier,
            role: profileData.user.role
        } : {}
    };

    return { success: true, data: mappedProfile, error: null };
  } catch (err) {
    console.error('Unexpected error in getSugarDaddyProfileDetails:', err);
    return { success: false, data: null, error: err };
  }
};

/**
 * Creates a new Sugar Baby profile for the given user ID.
 * Assumes the calling code has verified the user's permission (canCreateSugarBabyProfile).
 * RLS policy enforces that only verified escorts can insert for their own user ID.
 */
export const createSugarBabyProfile = async (
  userId: string,
  profileData: Omit<SugarBabyProfile, 'id' | 'userId' | 'createdAt' | 'updatedAt'>
): Promise<ServiceResult<SugarBabyProfile>> => {
  try {
    const { data, error } = await supabase
      .from('sugar_baby_profiles')
      .insert({ ...profileData, user_id: userId })
      .select()
      .single(); // Assuming the trigger updates the reference in profiles table

    if (error) {
      console.error('Error creating SB profile:', error);
      // Provide more specific error messages if possible (e.g., duplicate profile)
      if (error.code === '23505') { // unique_violation (likely user_id constraint)
          return { success: false, data: null, error: 'User already has a Sugar Baby profile.' };
      }
      return { success: false, data: null, error };
    }

    return { success: true, data: data as SugarBabyProfile, error: null };
  } catch (err) {
    console.error('Unexpected error in createSugarBabyProfile:', err);
    return { success: false, data: null, error: err };
  }
};

/**
 * Updates an existing Sugar Baby profile.
 * RLS policy enforces that users can only update their own profile.
 */
export const updateSugarBabyProfile = async (
  profileId: string,
  updates: Partial<Omit<SugarBabyProfile, 'id' | 'userId' | 'createdAt' | 'updatedAt'>>
): Promise<ServiceResult<SugarBabyProfile>> => {
  try {
    // Ensure userId, createdAt, etc. are not accidentally included in updates
    const cleanUpdates = { ...updates };
    delete (cleanUpdates as any).id;
    delete (cleanUpdates as any).userId;
    delete (cleanUpdates as any).createdAt;
    delete (cleanUpdates as any).updatedAt;

    const { data, error } = await supabase
      .from('sugar_baby_profiles')
      .update(cleanUpdates)
      .eq('id', profileId)
      .select()
      .single();

    if (error) {
      console.error('Error updating SB profile:', error);
      return { success: false, data: null, error };
    }

    return { success: true, data: data as SugarBabyProfile, error: null };
  } catch (err) {
    console.error('Unexpected error in updateSugarBabyProfile:', err);
    return { success: false, data: null, error: err };
  }
};

/**
 * Creates a new Sugar Daddy profile for the given user ID.
 * Assumes the calling code has verified the user's permission (canCreateSugarDaddyProfile).
 * RLS policy enforces that only regular/vip users can insert for their own user ID.
 * verificationStatus defaults to 'none' in the database.
 */
export const createSugarDaddyProfile = async (
  userId: string,
  profileData: Omit<SugarDaddyProfile, 'id' | 'userId' | 'verificationStatus' | 'createdAt' | 'updatedAt'>
): Promise<ServiceResult<SugarDaddyProfile>> => {
  try {
    const { data, error } = await supabase
      .from('sugar_daddy_profiles')
      .insert({ ...profileData, user_id: userId }) // verification_status has default
      .select()
      .single();

    if (error) {
      console.error('Error creating SD profile:', error);
       if (error.code === '23505') { // unique_violation (likely user_id constraint)
          return { success: false, data: null, error: 'User already has a Sugar Daddy profile.' };
      }
      return { success: false, data: null, error };
    }

    return { success: true, data: data as SugarDaddyProfile, error: null };
  } catch (err) {
    console.error('Unexpected error in createSugarDaddyProfile:', err);
    return { success: false, data: null, error: err };
  }
};

/**
 * Updates an existing Sugar Daddy profile.
 * RLS policy enforces that users can only update their own profile.
 * IMPORTANT: This function SHOULD NOT be used to update verificationStatus by non-admins.
 * Admin updates to verificationStatus should use a separate mechanism or RPC call.
 */
export const updateSugarDaddyProfile = async (
  profileId: string,
  updates: Partial<Omit<SugarDaddyProfile, 'id' | 'userId' | 'verificationStatus' | 'createdAt' | 'updatedAt'>>
): Promise<ServiceResult<SugarDaddyProfile>> => {
  try {
    // Explicitly remove verificationStatus to prevent accidental updates by non-admins
    const cleanUpdates = { ...updates };
    delete (cleanUpdates as any).id;
    delete (cleanUpdates as any).userId;
    delete (cleanUpdates as any).createdAt;
    delete (cleanUpdates as any).updatedAt;
    delete (cleanUpdates as any).verificationStatus; // Crucial step

    if (Object.keys(cleanUpdates).length === 0) {
        return { success: false, data: null, error: "No valid fields to update." };
    }

    const { data, error } = await supabase
      .from('sugar_daddy_profiles')
      .update(cleanUpdates)
      .eq('id', profileId)
      .select()
      .single();

    if (error) {
      console.error('Error updating SD profile:', error);
      return { success: false, data: null, error };
    }

    return { success: true, data: data as SugarDaddyProfile, error: null };
  } catch (err) {
    console.error('Unexpected error in updateSugarDaddyProfile:', err);
    return { success: false, data: null, error: err };
  }
};

// --- Listing Functions with Filters and Pagination --- 

/**
 * Lists Sugar Baby profiles with filtering and pagination.
 * RLS policy ensures only regular/vip users can select.
 */
export const listSugarBabyProfiles = async (
    filters: SugarBabyFilters = {},
    pagination: PaginationOptions = { page: 1, pageSize: 20 }
): Promise<ServiceResult<{ profiles: SugarBabyProfile[], totalCount: number }>> => {
    const { page = 1, pageSize = 20 } = pagination;
    const rangeFrom = (page - 1) * pageSize;
    const rangeTo = rangeFrom + pageSize - 1;

    try {
        let query = supabase
            .from('sugar_baby_profiles')
            .select(`
                id,
                pseudo,
                location,
                photos,
                lifestyle_interests,
                arrangement_expectations,
                user_id,
                profiles ( is_verified )
            `, { count: 'exact' }); // Request total count

        // Apply Filters
        if (filters.locationCity) {
            // Using jsonb containment for city within the location object
            // Note: Case-sensitive by default. Use .ilike within jsonb or a db function for case-insensitive.
             query = query.filter('location->>city', 'ilike', `%${filters.locationCity}%`);
        }
        if (filters.lifestyleInterests && filters.lifestyleInterests.length > 0) {
            query = query.overlaps('lifestyle_interests', filters.lifestyleInterests);
        }
        if (filters.arrangementTypes && filters.arrangementTypes.length > 0) {
            // Assumes arrangementExpectations is { type: string[], ... }
            query = query.overlaps('arrangement_expectations->type', filters.arrangementTypes);
        }
        // TODO: Add more filters (age from user profile, online status, etc.)

        // Apply Pagination
        query = query.range(rangeFrom, rangeTo);
        
        // Apply Sorting (Example: newest first)
        query = query.order('created_at', { ascending: false });

        const { data, error, count } = await query;

        if (error) {
            console.error('Error listing SB profiles:', error);
            return { success: false, data: null, error };
        }

        return { 
            success: true, 
            data: { profiles: data as SugarBabyProfile[], totalCount: count ?? 0 }, 
            error: null 
        };
    } catch (err) {
        console.error('Unexpected error in listSugarBabyProfiles:', err);
        return { success: false, data: null, error: err };
    }
};

/**
 * Lists Sugar Daddy profiles with filtering and pagination.
 * RLS policy ensures only regular/vip users can select.
 */
export const listSugarDaddyProfiles = async (
    filters: SugarDaddyFilters = {},
    pagination: PaginationOptions = { page: 1, pageSize: 20 }
): Promise<ServiceResult<{ profiles: SugarDaddyProfile[], totalCount: number }>> => {
     const { page = 1, pageSize = 20 } = pagination;
    const rangeFrom = (page - 1) * pageSize;
    const rangeTo = rangeFrom + pageSize - 1;

    try {
        let query = supabase
            .from('sugar_daddy_profiles')
            .select(`
                id,
                pseudo,
                age_range,
                location,
                photos,
                verification_status,
                lifestyle_interests,
                offered_arrangement,
                user_id,
                profiles ( subscription_tier )
            `, { count: 'exact' }); // Request total count

        // Apply Filters
         if (filters.locationCity) {
             query = query.filter('location->>city', 'ilike', `%${filters.locationCity}%`);
        }
         if (filters.ageRange) {
             query = query.eq('age_range', filters.ageRange); // Simple equality for now
        }
        if (filters.verificationStatus) {
            query = query.eq('verification_status', filters.verificationStatus);
        }
        if (filters.lifestyleInterests && filters.lifestyleInterests.length > 0) {
            query = query.overlaps('lifestyle_interests', filters.lifestyleInterests);
        }
        if (filters.supportTypes && filters.supportTypes.length > 0) {
             // Assumes offeredArrangement is { support_type: string[], ... }
             query = query.overlaps('offered_arrangement->supportType', filters.supportTypes);
        }
        if (filters.arrangementTypes && filters.arrangementTypes.length > 0) {
             // Assumes offeredArrangement is { ..., arrangement_type: string[] }
             query = query.overlaps('offered_arrangement->arrangementType', filters.arrangementTypes);
        }
         // TODO: Add more filters (profession keyword, VIP status from profiles table, etc.)

        // Apply Pagination
        query = query.range(rangeFrom, rangeTo);
        
        // Apply Sorting (Example: newest first)
        query = query.order('created_at', { ascending: false });

        const { data, error, count } = await query;

        if (error) {
            console.error('Error listing SD profiles:', error);
            return { success: false, data: null, error };
        }

        return { 
            success: true, 
            data: { profiles: data as SugarDaddyProfile[], totalCount: count ?? 0 }, 
            error: null 
        };
    } catch (err) {
        console.error('Unexpected error in listSugarDaddyProfiles:', err);
        return { success: false, data: null, error: err };
    }
}; 